// @givmo/passkeys — each console's `/sso/callback` (AS-WEB30): where the backend sends the browser back
// from the employer's sign-in page, with a one-use handoff code or an error (frame W4). It only ever
// says "Signing you in…"; what happens next is the console's:
//
//   - IN THE COMPANY'S WINDOW (the popup path): hand the answer to the page that opened it
//     (`organization-sso-return.ts`, two ways) and close. A window that cannot close itself keeps
//     saying it is signing the person in, which their own tab is doing.
//   - IN THE SAME TAB (a refused popup): this tab kept the attempt (the PKCE verifier, the typed
//     address, where to land), so it finishes here, then the console lands the person. A refusal,
//     or an attempt older than the backend's flow, goes back to the console's step page with the
//     address and the reason (`onFailed`; WF1–WF5), in the register the step was in.
//   - A visit with neither an attempt nor an answer goes to the step page, fresh.
//
// ⚠ IT RUNS ONCE PER ANSWER, NOT PER MOUNT. A console may remount its whole router when the signed-in
// account changes (the employer portal's identity boundary), which is the very thing this page causes;
// StrictMode mounts twice. So the work and its outcome live in a module-level store keyed by the answer,
// a remounted page reads the outcome instead of starting again, the attempt is taken once, and each
// outcome is handed to the console once.

import { useEffect, useSyncExternalStore } from 'react';
import { Button, cn, testIdProp } from '@givmo/design-system';
import { AuthError } from '@givmo/auth';
import { ORGANIZATION_SSO_WORDS as W } from './organization-sso-copy';
import {
  organizationSsoFailureOf,
  organizationSsoFailureOfCode,
  type OrganizationSsoFailure,
} from './organization-sso-transport';
import {
  publishOrganizationSsoResult,
  takeOrganizationSsoAttempt,
  type OrganizationSsoAttempt,
} from './organization-sso-return';

/** Where a return that could not finish goes: the step, in its register, with what was typed. */
export interface OrganizationSsoCallbackFailure {
  register: OrganizationSsoAttempt['register'];
  email: string;
  /** Why; `null` for a visit that carried nothing (a fresh step). */
  failure: OrganizationSsoFailure | null;
  /** Where the step was going to land; `null` when no attempt said. */
  returnTo: string | null;
  /** Where the step's way back went (cut 1007e's review, F5); `null` for the console's own door. */
  back: string | null;
}

type Outcome =
  | { kind: 'relayed' }
  | { kind: 'signingIn' }
  | { kind: 'signedIn'; returnTo: string; result: unknown }
  | { kind: 'failed'; failed: OrganizationSsoCallbackFailure };

interface Handled {
  key: string;
  outcome: Outcome;
  /** Whether a mounted page has handed this outcome to its console (once, across remounts). */
  delivered: boolean;
}

let current: Handled | null = null;
const listeners = new Set<() => void>();

function publish(key: string, outcome: Outcome): void {
  current = { key, outcome, delivered: false };
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const read = () => current;

/** Forget the handled answer. A TEST SEAM: each case renders a fresh callback. */
export function resetOrganizationSsoCallback(): void {
  current = null;
}

export interface OrganizationSsoCallbackProps<T> {
  /** The callback's query, read by the host route: the handoff code, or the backend's error code. */
  handoffCode: string | null;
  error: string | null;
  /** The door's own heading look. */
  headingClass: string;
  /** The console's sign-in with the handoff (its auth context's `signInWithOrganizationSso`). */
  signIn: (params: { handoffCode: string; codeVerifier: string }) => Promise<T>;
  /** Signed in here (the same-tab path): the console lands the person where the attempt named. */
  onSignedIn: (returnTo: string, result: T) => void;
  /** It could not finish here: the console opens its step page in the attempt's register. */
  onFailed: (failed: OrganizationSsoCallbackFailure) => void;
  /** Called once the answer is read, so the console can take the code out of the address bar. */
  onRead?: () => void;
  /**
   * `director`: drawn inside Board's director shell, whose page title is the step's, so the card
   * leaves its own heading out (cut 1007e's fold, Board's B-F3).
   */
  register?: 'console' | 'director';
}

function failureOfSignIn(thrown: unknown): OrganizationSsoFailure {
  // A Firebase failure of the custom-token sign-in: nothing the person can fix but trying again.
  if (thrown instanceof AuthError) return 'didNotFinish';
  return organizationSsoFailureOf(thrown);
}

function failedFor(
  attempt: OrganizationSsoAttempt,
  failure: OrganizationSsoFailure,
): OrganizationSsoCallbackFailure {
  return {
    register: attempt.register,
    email: attempt.email,
    failure,
    returnTo: attempt.returnTo,
    back: attempt.back ?? null,
  };
}

/** Start the work for this answer, once (see the header). */
function handle<T>(
  key: string,
  handoffCode: string | null,
  error: string | null,
  signIn: OrganizationSsoCallbackProps<T>['signIn'],
): void {
  if (current?.key === key) return;
  const kept = takeOrganizationSsoAttempt();
  if (kept === null) {
    if (handoffCode === null && error === null) {
      publish(key, {
        kind: 'failed',
        failed: { register: 'console', email: '', failure: null, returnTo: null, back: null },
      });
      return;
    }
    // The company's window: hand the answer back and close. A window that cannot close itself keeps
    // saying it is signing the person in (their own tab is).
    publishOrganizationSsoResult({ handoffCode, error });
    publish(key, { kind: 'relayed' });
    try {
      globalThis.close();
    } catch {
      // Not script-closable: the line on screen tells the person what is happening.
    }
    return;
  }
  const { attempt, expired } = kept;
  if (expired) {
    // This tab went to the company's page itself, longer ago than the backend keeps the flow: its
    // verifier can no longer be exchanged, whatever came back.
    publish(key, { kind: 'failed', failed: failedFor(attempt, 'didNotFinish') });
    return;
  }
  if (handoffCode === null) {
    publish(key, {
      kind: 'failed',
      failed: failedFor(attempt, organizationSsoFailureOfCode(error)),
    });
    return;
  }
  publish(key, { kind: 'signingIn' });
  signIn({ handoffCode, codeVerifier: attempt.codeVerifier }).then(
    (result) => publish(key, { kind: 'signedIn', returnTo: attempt.returnTo, result }),
    (thrown: unknown) =>
      publish(key, { kind: 'failed', failed: failedFor(attempt, failureOfSignIn(thrown)) }),
  );
}

export function OrganizationSsoCallback<T>({
  handoffCode,
  error,
  headingClass,
  signIn,
  onSignedIn,
  onFailed,
  onRead,
  register = 'console',
}: OrganizationSsoCallbackProps<T>) {
  const key = `${handoffCode ?? ''}|${error ?? ''}`;
  const handled = useSyncExternalStore(subscribe, read, read);

  useEffect(() => {
    // An address with no answer after one was handled is the host taking the code out of the address
    // bar: keep the outcome rather than starting a fresh step over it.
    if (handoffCode === null && error === null && current !== null) return;
    handle(key, handoffCode, error, signIn);
    if (handoffCode !== null || error !== null) onRead?.();
  }, [key, handoffCode, error, signIn, onRead]);

  useEffect(() => {
    if (handled === null || handled.delivered) return;
    const { outcome } = handled;
    if (outcome.kind === 'signedIn') {
      handled.delivered = true;
      onSignedIn(outcome.returnTo, outcome.result as T);
    } else if (outcome.kind === 'failed') {
      handled.delivered = true;
      onFailed(outcome.failed);
    }
  }, [handled, onSignedIn, onFailed]);

  return (
    <div className="flex flex-col gap-20" {...testIdProp('organization-sso-signing-in')}>
      <div className="flex flex-col gap-4">
        {register !== 'director' && <h1 className={cn(headingClass, 'text-balance')}>{W.title}</h1>}
        <p
          className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
          role="status"
        >
          {W.signingIn}
        </p>
      </div>
      <Button appearance="saas" tone="primary" size="lg" fullWidth loading>
        {W.continue}
      </Button>
    </div>
  );
}
